/**
 * Header Rule Processor Strategy Pattern
 * Allows extensible header rule processing
 */

import type { InternalAxiosRequestConfig } from "axios";
import type { HeaderRule } from "../types";

/**
 * Header Rule Processor Interface
 * Strategy pattern for processing different types of header rules
 */
export interface HeaderRuleProcessor {
  /**
   * Check if this processor can handle the given rule
   */
  canProcess(rule: HeaderRule): boolean;

  /**
   * Process the rule and modify the request config
   */
  process(rule: HeaderRule, config: InternalAxiosRequestConfig): Promise<void>;

  /**
   * Priority for processor execution (higher = first)
   */
  priority: number;
}

/**
 * Remove Headers Processor
 * Handles header removal rules
 */
export class RemoveHeaderProcessor implements HeaderRuleProcessor {
  priority = 100; // High priority - remove first

  canProcess(rule: HeaderRule): boolean {
    return !!rule.remove && rule.remove.length > 0;
  }

  async process(
    rule: HeaderRule,
    config: InternalAxiosRequestConfig,
  ): Promise<void> {
    if (!rule.remove || !config.headers) {
      return;
    }

    rule.remove.forEach((header) => {
      config.headers?.delete(header);
    });
  }
}

/**
 * Set Headers Processor
 * Handles header override rules
 */
export class SetHeaderProcessor implements HeaderRuleProcessor {
  priority = 50; // Medium priority - set after remove

  canProcess(rule: HeaderRule): boolean {
    return !!rule.set && Object.keys(rule.set).length > 0;
  }

  async process(
    rule: HeaderRule,
    config: InternalAxiosRequestConfig,
  ): Promise<void> {
    if (!rule.set || !config.headers) {
      return;
    }

    for (const [key, value] of Object.entries(rule.set)) {
      try {
        const headerValue =
          typeof value === "function" ? await Promise.resolve(value()) : value;

        if (headerValue !== null && headerValue !== undefined) {
          config.headers.set(key, headerValue);
        }
      } catch (error) {
        console.warn(
          `[API] Error applying header rule (set) for "${key}":`,
          error,
        );
      }
    }
  }
}

/**
 * Add Headers Processor
 * Handles header addition rules (only if not already present)
 */
export class AddHeaderProcessor implements HeaderRuleProcessor {
  priority = 25; // Lower priority - add last

  canProcess(rule: HeaderRule): boolean {
    return !!rule.add && Object.keys(rule.add).length > 0;
  }

  async process(
    rule: HeaderRule,
    config: InternalAxiosRequestConfig,
  ): Promise<void> {
    if (!rule.add || !config.headers) {
      return;
    }

    for (const [key, value] of Object.entries(rule.add)) {
      if (!config.headers.has(key)) {
        try {
          const headerValue =
            typeof value === "function"
              ? await Promise.resolve(value())
              : value;

          if (headerValue !== null && headerValue !== undefined) {
            config.headers.set(key, headerValue);
          }
        } catch (error) {
          console.warn(
            `[API] Error applying header rule (add) for "${key}":`,
            error,
          );
        }
      }
    }
  }
}

/**
 * Header Rule Engine
 * Orchestrates header rule processing using strategy pattern
 */
export class HeaderRuleEngine {
  private processors: HeaderRuleProcessor[] = [];

  constructor() {
    // Register default processors
    this.register(new RemoveHeaderProcessor());
    this.register(new SetHeaderProcessor());
    this.register(new AddHeaderProcessor());
  }

  /**
   * Register a custom header rule processor
   */
  register(processor: HeaderRuleProcessor): void {
    this.processors.push(processor);
    // Sort by priority (higher first)
    this.processors.sort((a, b) => b.priority - a.priority);
  }

  /**
   * Process a header rule using registered processors
   */
  async processRule(
    rule: HeaderRule,
    config: InternalAxiosRequestConfig,
  ): Promise<void> {
    for (const processor of this.processors) {
      if (processor.canProcess(rule)) {
        await processor.process(rule, config);
      }
    }
  }

  /**
   * Process multiple header rules
   */
  async processRules(
    rules: HeaderRule[],
    config: InternalAxiosRequestConfig,
  ): Promise<void> {
    // Sort rules by priority (higher first)
    const sortedRules = [...rules].sort(
      (a, b) => (b.priority || 0) - (a.priority || 0),
    );

    const url = config.url || "";
    const baseURL = config.baseURL || "";
    const fullUrl = `${baseURL}${url}`;

    for (const rule of sortedRules) {
      // Check if pattern matches
      const matches =
        typeof rule.pattern === "string"
          ? fullUrl.includes(rule.pattern) || url.includes(rule.pattern)
          : rule.pattern.test(fullUrl) || rule.pattern.test(url);

      if (matches) {
        await this.processRule(rule, config);
      }
    }
  }
}
